import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import * as DTO from "../../../interfaces";
import { useSocialCoachSession, URLS, userFullName, printBasicDate, useAccountSettings } from "@/utils";
import { useEffect, useMemo, useState } from "react";
import { AssistantServices } from "@/services";
import { Combobox, Button, InputGroup, Input, Badge, Table, Spinner, ContactCell } from "@/components/ui";
import { Search } from "lucide-react";
import { WebComponents } from "@/interfaces";

type Props = RouteComponentProps;

type AssistantWithStatus = DTO.Assistant & { status: string };

const AssistantsFC: React.FC<Props> = ({ history }) => {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.ASSISTANTS);

  const [searchTerm, setSearchTerm] = useState("");

  const statusOptions = [
    { key: "ALL", label: "All", value: "ALL" },
    { key: "Active", label: "Active", value: "ACTIVE" },
    { key: "Cancelled", label: "Cancelled", value: "CANCELLED" },
    { key: "Pending", label: "Pending", value: "PENDING" },
  ];

  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);

  const [activeAssistants, setActiveAssistants] = useState<DTO.Assistant[]>([]);
  const [cancelledAssistants, setCancelledAssistants] = useState<DTO.Assistant[]>([]);
  const [pendingAssistants, setPendingAssistants] = useState<DTO.Assistant[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadAll = async () => {
      if (coachId) {
        setLoading(true);
        try {
          const [active, cancelled, pending] = await Promise.all([
            AssistantServices.getByCoachId(coachId.toString()),
            AssistantServices.getInactive(coachId.toString()),
            AssistantServices.getPending(coachId.toString()),
          ]);

          setActiveAssistants(active || []);
          setCancelledAssistants(cancelled || []);
          setPendingAssistants(pending || []);
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      }
    };

    loadAll();
  }, [coachId]);

  const allAssistants = useMemo((): AssistantWithStatus[] => {
    return [
      ...activeAssistants.map(a => ({ ...a, status: "Active" })),
      ...pendingAssistants.map(a => ({ ...a, status: "Pending" })),
      ...cancelledAssistants.map(a => ({ ...a, status: "Cancelled" })),
    ];
  }, [activeAssistants, pendingAssistants, cancelledAssistants]);

  const filteredAssistants = useMemo(() => {
    let result = allAssistants;

    if (selectedStatus.value !== "ALL") {
      result = result.filter(a => a.status.toUpperCase() === selectedStatus.value);
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(a => {
        const values = [
          a.account?.profile?.firstName,
          a.account?.profile?.lastName,
          a.account?.user?.email,
          ...(a.regionNames || []),
          ...(a.branchNames || []),
        ];
        return values.some(v => v?.toLowerCase()?.includes(lowerSearch));
      });
    }

    return result;
  }, [searchTerm, allAssistants, selectedStatus]);

  const getSegmentNames = (assistant: DTO.Assistant) => {
    const types = ["REGION", "BRANCH"];
    const segments = assistant.segmentationDetails
      ?.filter(s => types.includes(s.type))
      ?.sort((a, b) => b.type.localeCompare(a.type) || (a.name || "").localeCompare(b.name || ""));

    if (segments?.length > 0)
      return (
        <div className="flex flex-row flex-wrap gap-1">
          {segments?.map(s => (
            <Badge
              key={`segmentBadge-${s.type}-${s.name}`}
              size={"small"}
              variant={"outline"}
              className={s.type === "REGION" ? "bg-pink-100" : "bg-blue-100"}
            >
              <div className="font-medium text-[10px]">{s.name}</div>
            </Badge>
          ))}
        </div>
      );
    else return "";
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <InputGroup
            className="w-80"
            control={controlProps => (
              <Input
                {...controlProps}
                placeholder="Search Assistant..."
                onChange={e => setSearchTerm(e.target.value)}
              />
            )}
            append={<Search className="w-4 h-4 text-muted-foreground" />}
          />

          <Combobox
            className="w-50"
            items={statusOptions}
            value={selectedStatus}
            onValueChange={data => setSelectedStatus(data ?? statusOptions[0])}
          />
          {loading && <Spinner className="w-4 h-4" />}
        </div>
        {withWritePermission && (
          <Button onClick={() => history.push(URLS.coach.newAssistant)} children="Add Assistant" />
        )}
      </div>

      <Table>
        <Table.Header>
          <Table.Row>
            <Table.Head>Assistant</Table.Head>
            <Table.Head>Status</Table.Head>
            <Table.Head>
              <div className={"flex flex-row gap-1"}>
                <Badge size={"small"} variant={"outline"} className={"bg-pink-100"}>
                  Regions
                </Badge>
                <div>/</div>
                <Badge size={"small"} variant={"outline"} className={"bg-blue-100"}>
                  Branches
                </Badge>
              </div>
            </Table.Head>
            <Table.Head>Added</Table.Head>
            <Table.Head>Last Login</Table.Head>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {filteredAssistants.length > 0
            ? filteredAssistants.map(assistant => {
                const goToAssistantDetail = () => {
                  const assistantUrl = URLS.coach.editAssistant.replace(
                    ":userId",
                    `${assistant?.account?.user?.userId}`
                  );
                  history.push(assistantUrl);
                };
                return (
                  <Table.Row
                    key={`assistantRow-${assistant?.account?.user?.userId}`}
                    className="cursor-pointer"
                    onClick={goToAssistantDetail}
                  >
                    <Table.Cell>
                      <ContactCell
                        showAvatar
                        name={assistant.account?.profile ? userFullName(assistant.account.profile) : ""}
                        email={assistant.account?.user?.email}
                        imageUrl={assistant.account?.profile?.photoUrl}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="capitalize">{assistant.status}</div>
                    </Table.Cell>
                    <Table.Cell>{getSegmentNames(assistant)}</Table.Cell>
                    <Table.Cell className="text-muted-foreground">
                      {printBasicDate((assistant?.account?.user as any)?.createdDate)}
                    </Table.Cell>
                    <Table.Cell className="text-muted-foreground">
                      {assistant?.account?.user?.latestLoginDate &&
                        printBasicDate(assistant?.account?.user?.latestLoginDate!)}
                    </Table.Cell>
                  </Table.Row>
                );
              })
            : !loading && (
                <Table.Row>
                  <Table.Cell colSpan={5} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center gap-1">
                      <Search className="w-8 h-8 text-muted-foreground/50" />
                      <div className="font-semibold">No results</div>
                      <div className="text-sm text-muted-foreground">There are no assistants in this section</div>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
        </Table.Body>
      </Table>

      {loading && filteredAssistants.length === 0 && (
        <div className="flex items-center justify-center p-12">
          <Spinner className="w-8 h-8" />
        </div>
      )}
    </div>
  );
};

export const Assistants = withRouter(AssistantsFC);
